import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { MapContainer, TileLayer, useMapEvents, useMap } from "react-leaflet";
import { useMemo, useEffect } from "react";
import { UserSelectionMarker } from "./BryxMarker";
import { useConstCallback } from "@bryxinc/ts-utils";
import * as L from "leaflet";
import "leaflet/dist/leaflet.css";
const MB_STYLE_URL = "https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token={accessToken}";
const MB_ATTRIBUTION = 'Data &copy; <a href="http://bryx.com">Bryx, Inc.</a> | Imagery from <a href="http://mapbox.com/about/maps/">MapBox</a> | Map &copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>';
const MB_ACCESS_TOKEN = "pk.eyJ1IjoidGVicm93biIsImEiOiIzb0xlTjlzIn0.qRGtH1Q-ZAViez0fTPX9fg";
const UNITED_STATES = {
    coordinates: [
        [
            [-78.81881031321555, 24.832387990859218],
            [-78.16343957863495, 31.917910622557812],
            [-73.9352833479162, 35.75007544972206],
            [-69.10555028263238, 40.87197738433835],
            [-63.3422076091089, 44.90767860219589],
            [-81.39665977658645, 51.6996479561011],
            [-131.30168400593706, 50.79815383867475],
            [-131.7745954418622, 43.35525535392432],
            [-122.65733712059287, 32.58123987172266],
            [-115.27225408488067, 30.15689531597424],
            [-98.05333484474932, 25.095828678862745],
            [-78.81881031321555, 24.832387990859218],
        ],
    ],
    type: "Polygon",
};
export const BryxMap = (props) => {
    const { center: jsonCenter, children } = props;
    const centerGeometry = useMemo(() => {
        const featureGroup = L.geoJSON(jsonCenter ?? UNITED_STATES);
        const centerBounds = featureGroup.getBounds();
        return { center: centerBounds.getCenter(), bounds: centerBounds };
    }, [jsonCenter]);
    return (_jsxs(MapContainer, { maxZoom: 18, bounds: centerGeometry.bounds, style: { height: "100%", width: "100%" }, children: [_jsx(BryxMapBehavior, { centerGeometry: centerGeometry, ...props }), _jsx(TileLayer, { maxZoom: 18, url: MB_STYLE_URL, accessToken: MB_ACCESS_TOKEN, id: "tebrown/clfvaa9h1002e01pchml9t1da", attribution: MB_ATTRIBUTION }), children] }));
};
BryxMap.useMap = useMap;
BryxMap.useMapEvents = useMapEvents;
function latLngToGeoJSON(input) {
    return [input.lng, input.lat];
}
const BryxMapBehavior = ({ centerGeometry, onZoomChange, onCenterChange, userSelection, onUserSelectionChange, defaultZoom = 18, }) => {
    const map = useMapEvents({
        click: useConstCallback((event) => {
            if (!onUserSelectionChange) {
                return;
            }
            if (event.originalEvent.shiftKey) {
                const selectionGeometry = {
                    coordinates: [[]],
                    type: "Polygon",
                };
                if (userSelection && userSelection.type == "Point") {
                    // Start new polygon, including old Point
                    selectionGeometry.coordinates[0].push([userSelection.coordinates[0], userSelection.coordinates[1]]);
                }
                else if (userSelection && userSelection.type == "Polygon") {
                    for (const [lng, lat] of userSelection.coordinates[0]) {
                        selectionGeometry.coordinates[0].push([lng, lat]);
                    }
                }
                selectionGeometry.coordinates[0].push([event.latlng.lng, event.latlng.lat]);
                onUserSelectionChange(selectionGeometry);
            }
            else {
                const selectionGeometry = {
                    coordinates: [event.latlng.lng, event.latlng.lat],
                    type: "Point",
                };
                onUserSelectionChange(selectionGeometry);
            }
        }),
        zoomend: useConstCallback((_event) => {
            onZoomChange(map.getZoom());
        }),
        moveend: useConstCallback((_event) => {
            if (onCenterChange) {
                const center = map.getBounds();
                if (center.equals(centerGeometry.bounds)) {
                    return;
                }
                onCenterChange({
                    type: "Polygon",
                    coordinates: [
                        [
                            latLngToGeoJSON(center.getNorthEast()),
                            latLngToGeoJSON(center.getNorthWest()),
                            latLngToGeoJSON(center.getSouthWest()),
                            latLngToGeoJSON(center.getSouthEast()),
                            latLngToGeoJSON(center.getNorthEast()),
                        ],
                    ],
                });
            }
        }),
    });
    useEffect(() => {
        map.setZoom(defaultZoom);
    }, []);
    useEffect(() => {
        // map.flyToBounds(centerGeometry.bounds); // uncomment to enable glide animation
        map.fitBounds(centerGeometry.bounds); // don't animate, just jump to position
    }, [centerGeometry.bounds]);
    const onClickUserSelection = useConstCallback((_event, lat, lng) => {
        if (!userSelection || userSelection.type != "Polygon" || !onUserSelectionChange) {
            return;
        }
        const firstCoordinates = userSelection.coordinates[0][0];
        // Selection is being finalized
        if (firstCoordinates[0] == lng && firstCoordinates[1] == lat) {
            const selectionGeometry = {
                type: "Polygon",
                coordinates: [userSelection.coordinates[0].map((position) => Array.from(position))],
            };
            selectionGeometry.coordinates[0].push([lng, lat]);
            onUserSelectionChange(selectionGeometry);
        }
    });
    return _jsx(_Fragment, { children: userSelection && onUserSelectionChange && _jsx(UserSelectionMarker, { geometry: userSelection, onClick: onClickUserSelection }) });
};
